import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Car, MapPin, Calendar, Search, Users, Fuel, Settings2, Loader2, Gauge, Shield, Crown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { toast } from 'sonner';
import { useCurrency } from '@/lib/currency-context';
import { carBroker } from '@/lib/api';
import { cn } from '@/lib/utils';
import { buildPathForLocale } from '@/lib/intl-seo';
import { PageSeo } from '@/components/seo/PageSeo';
import {
  vehicleImageUrl,
  vehicleToken,
  vehicleVendorName,
  vehicleVendorLogo,
  vehicleSeatsLabel,
  vehicleTransmissionLabel,
  vehicleFuelLabel,
  vehicleCategoryLabel,
  vehicleTypeLabel,
  vehicleConditions,
} from '@/lib/carBrokerVehicle';

function toApiDate(isoDate) {
  if (!isoDate) return '';
  const [y, m, d] = isoDate.split('-');
  return `${d}.${m}.${y}`;
}

function defaultPickupIso() {
  const d = new Date();
  d.setDate(d.getDate() + 3);
  return d.toISOString().slice(0, 10);
}

function defaultReturnIso() {
  const d = new Date();
  d.setDate(d.getDate() + 6);
  return d.toISOString().slice(0, 10);
}

function extractVehicles(payload) {
  const data = payload?.data ?? payload;
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.vehicles)) return data.vehicles;
  if (Array.isArray(data.Vehicles)) return data.Vehicles;
  if (Array.isArray(data.items)) return data.items;
  return [];
}

function vehicleKey(v, idx) {
  return (
    vehicleToken(v) ||
    `${v.vehicleId || v.vehicleName || v.sippCode || 'car'}-${idx}`
  );
}

function countryCodeOf(x) {
  return String(x?.countryCode || x?.country_code || '').toUpperCase();
}

function filterLocationsLocal(list, q) {
  if (!Array.isArray(list) || !list.length) return [];
  const needle = (q || '').trim().toLowerCase();
  if (!needle) {
    // TR first, then airports, then name — full worldwide list
    return [...list].sort((a, b) => {
      const trA = countryCodeOf(a) === 'TR' ? 0 : 1;
      const trB = countryCodeOf(b) === 'TR' ? 0 : 1;
      if (trA !== trB) return trA - trB;
      const aa = a.isAirport ? 0 : 1;
      const bb = b.isAirport ? 0 : 1;
      if (aa !== bb) return aa - bb;
      return String(a.locationName || '').localeCompare(String(b.locationName || ''));
    });
  }
  const scored = [];
  for (const x of list) {
    const name = String(x.locationName || '').toLowerCase();
    const iata = String(x.iataCode || '').toLowerCase();
    const city = String(x.cityName || x.city || '').toLowerCase();
    const country = countryCodeOf(x).toLowerCase();
    const hay = `${name} ${iata} ${city} ${country}`;
    if (!hay.includes(needle)) continue;
    let rank = 2;
    if (iata === needle) rank = 0;
    else if (name.startsWith(needle) || iata.startsWith(needle)) rank = 1;
    // Prefer Turkey matches slightly when same rank
    const trBoost = countryCodeOf(x) === 'TR' ? 0 : 1;
    scored.push({ rank, trBoost, name, x });
  }
  scored.sort(
    (a, b) => a.rank - b.rank || a.trBoost - b.trBoost || a.name.localeCompare(b.name)
  );
  return scored.map((s) => s.x);
}

function LocationPicker({
  label,
  selectedLabel,
  onSelect,
  placeholder,
  catalog,
  catalogLoading,
  ensureCatalog,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (open) ensureCatalog?.();
  }, [open, ensureCatalog]);

  const options = filterLocationsLocal(catalog, query);

  return (
    <div className="space-y-1.5">
      {label ? <Label>{label}</Label> : null}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            className="w-full justify-between font-normal h-11"
          >
            <span className="truncate text-left">
              {selectedLabel || placeholder}
            </span>
            <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[min(100vw-2rem,360px)] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={placeholder}
              value={query}
              onValueChange={setQuery}
            />
            <CommandList className="max-h-72">
              {catalogLoading && options.length === 0 ? (
                <div className="flex items-center gap-2 px-3 py-4 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  …
                </div>
              ) : (
                <>
                  <CommandEmpty>—</CommandEmpty>
                  <CommandGroup>
                    {options.map((loc) => (
                      <CommandItem
                        key={loc.locationId}
                        value={`${loc.locationId}-${loc.locationName}`}
                        onSelect={() => {
                          onSelect(loc);
                          setOpen(false);
                          setQuery('');
                        }}
                      >
                        <MapPin className="mr-2 h-4 w-4 shrink-0" />
                        <span className="truncate">
                          {loc.locationName}
                          {loc.iataCode ? ` (${loc.iataCode})` : ''}
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                  {catalogLoading && (
                    <div className="flex items-center gap-2 border-t px-3 py-2 text-xs text-muted-foreground sticky bottom-0 bg-popover">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      {options.length}… yükleniyor
                    </div>
                  )}
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function locLabel(loc) {
  if (!loc) return '';
  return `${loc.locationName}${loc.iataCode ? ` (${loc.iataCode})` : ''}`;
}

export const CarRentalsPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const lc = (i18n.language || 'tr').slice(0, 2);

  const [searching, setSearching] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [searched, setSearched] = useState(false);
  const [locationCatalog, setLocationCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const catalogLoadedRef = React.useRef(false);
  const catalogLoadingRef = React.useRef(false);

  const [form, setForm] = useState({
    pickupLocationId: '',
    returnLocationId: '',
    pickupLoc: null,
    returnLoc: null,
    sameReturn: true,
    pickupDate: defaultPickupIso(),
    returnDate: defaultReturnIso(),
    pickupTime: '10:00',
    returnTime: '10:00',
    driverAge: '30',
    // Always search EUR (KolayCAR: TRY often empty). Display via navbar currency.
    currencyCode: 'EUR',
  });

  const langCode = (i18n.language || 'tr').slice(0, 2).toUpperCase();
  const apiLang = langCode === 'EN' ? 'EN' : 'TR';

  const pickupLoc = form.pickupLoc;
  const returnLoc = form.sameReturn ? form.pickupLoc : form.returnLoc;

  const ensureCatalog = useCallback(async () => {
    if (catalogLoadedRef.current || catalogLoadingRef.current) return;
    catalogLoadingRef.current = true;
    setCatalogLoading(true);
    try {
      // 1) Quick: TR airports first (select usable immediately)
      const quick = await carBroker.locations({
        languageCode: apiLang,
        countryCode: 'TR',
        airportsOnly: true,
        limit: 0,
      });
      const airports = Array.isArray(quick.data?.data) ? quick.data.data : [];
      if (airports.length) setLocationCatalog(airports);

      // 2) Full worldwide catalog (total ~1886) — no country filter
      const full = await carBroker.locations({
        languageCode: apiLang,
        limit: 0,
      });
      const all = Array.isArray(full.data?.data) ? full.data.data : airports;
      setLocationCatalog(all);
      catalogLoadedRef.current = true;
    } catch (err) {
      console.error(err);
      toast.error(t('carRental.brokerLoadError', 'Lokasyonlar yüklenemedi'));
    } finally {
      catalogLoadingRef.current = false;
      setCatalogLoading(false);
    }
  }, [apiLang, t]);

  // Start loading in background as soon as page opens (form stays visible)
  useEffect(() => {
    ensureCatalog();
  }, [ensureCatalog]);

  const handleSearch = async () => {
    if (!form.pickupLocationId) {
      toast.error(t('carRental.selectPickup', 'Teslim alma noktası seçin'));
      return;
    }
    if (!form.sameReturn && !form.returnLocationId) {
      toast.error(t('carRental.selectReturn', 'İade noktası seçin'));
      return;
    }
    if (!form.pickupDate || !form.returnDate) {
      toast.error(t('carRental.selectDates'));
      return;
    }

    setSearching(true);
    setSearched(true);
    try {
      const res = await carBroker.searchVehicles({
        languageCode: apiLang,
        currencyCode: form.currencyCode,
        pickupLocationId: Number(form.pickupLocationId),
        returnLocationId: Number(
          form.sameReturn ? form.pickupLocationId : form.returnLocationId
        ),
        pickupDate: toApiDate(form.pickupDate),
        returnDate: toApiDate(form.returnDate),
        pickupTime: form.pickupTime,
        returnTime: form.returnTime,
        driverAge: Number(form.driverAge) || 30,
      });
      const list = extractVehicles(res.data);
      setVehicles(list);
      if (res.data?.currencyFallback && res.data?.resolvedCurrency) {
        setForm((f) => ({ ...f, currencyCode: res.data.resolvedCurrency }));
        toast.message(
          t(
            'carRental.currencyFallback',
            `${res.data.requestedCurrency} sonuç vermedi; ${res.data.resolvedCurrency} ile gösteriliyor.`
          )
        );
      }
      if (!list.length) {
        toast.message(
          res.data?.hint || t('carRental.noCarsFound')
        );
      }
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.detail ||
        t('carRental.searchError', 'Araç araması başarısız');
      toast.error(typeof msg === 'string' ? msg : t('common.error'));
      setVehicles([]);
    } finally {
      setSearching(false);
    }
  };

  const selectVehicle = (vehicle) => {
    const token = vehicleToken(vehicle);
    if (!token) {
      toast.error(t('carRental.missingToken', 'Rezervasyon token bulunamadı'));
      return;
    }
    const minAge = Number(vehicle.vendorMinimumDriverAge || 0);
    const driverAge = Number(form.driverAge) || 30;
    if (minAge && driverAge < minAge) {
      toast.error(
        t(
          'carRental.driverAgeTooLow',
          `Bu araç için minimum sürücü yaşı ${minAge}`
        )
      );
      return;
    }
    const payload = {
      vehicle,
      search: {
        pickupLocationId: Number(
          vehicle.pickupLocationId || form.pickupLocationId
        ),
        returnLocationId: Number(
          vehicle.returnLocationId ||
            (form.sameReturn ? form.pickupLocationId : form.returnLocationId)
        ),
        pickupLocationName:
          vehicle.pickupLocationName || pickupLoc?.locationName,
        returnLocationName:
          vehicle.returnLocationName || returnLoc?.locationName,
        pickupDate: toApiDate(form.pickupDate),
        returnDate: toApiDate(form.returnDate),
        pickupDateIso: form.pickupDate,
        returnDateIso: form.returnDate,
        pickupTime: form.pickupTime,
        returnTime: form.returnTime,
        languageCode: apiLang,
        currencyCode: vehicle.currencyCode || form.currencyCode,
        driverAge,
      },
    };
    sessionStorage.setItem('pendingCarBrokerBooking', JSON.stringify(payload));
    navigate(buildPathForLocale(lc, ['car-rentals', 'booking']));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-slate-50">
      <PageSeo title={t('carRental.title')} description={t('carRental.heroSubtitle')} segments={['car-rentals']} />
      <section className="relative py-12 md:py-16 overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 20%, #bae6fd 0%, transparent 45%), radial-gradient(circle at 80% 0%, #fde68a 0%, transparent 40%)',
          }}
        />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-sky-100 text-sky-800 px-4 py-2 rounded-full mb-4">
              <Car className="w-5 h-5" />
              <span className="text-sm font-medium">{t('carRental.title')}</span>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch justify-center gap-3 mb-4">
              <Button
                type="button"
                size="lg"
                className="h-auto min-h-14 px-6 py-3 text-base md:text-lg font-serif font-bold bg-sky-700 hover:bg-sky-800"
              >
                <Car className="w-5 h-5 mr-2 shrink-0" />
                {t('carRental.heroTitle')}
              </Button>
              <Button
                type="button"
                size="lg"
                variant="outline"
                className="h-auto min-h-14 px-6 py-3 text-base md:text-lg font-serif font-bold border-amber-400 bg-white text-black hover:bg-amber-50 hover:text-black"
                onClick={() => navigate(buildPathForLocale(lc, ['taxi-limousine']))}
              >
                <Crown className="w-5 h-5 mr-2 shrink-0" />
                {t('carRental.heroLimousineTitle', 'Hayalinizdeki Limuzini Kiralayın')}
              </Button>
            </div>
            <p className="text-muted-foreground">
              {t('carRental.heroSubtitle')}
            </p>
          </div>

          <Card className="max-w-5xl mx-auto shadow-lg border-sky-100/80">
            <CardContent className="p-5 md:p-6 space-y-4">
                <>
                  <div className="grid md:grid-cols-2 gap-4">
                    <LocationPicker
                      label={t('carRental.pickupLocation', 'Teslim alma')}
                      selectedLabel={locLabel(pickupLoc)}
                      catalog={locationCatalog}
                      catalogLoading={catalogLoading}
                      ensureCatalog={ensureCatalog}
                      onSelect={(loc) =>
                        setForm((f) => ({
                          ...f,
                          pickupLocationId: String(loc.locationId),
                          pickupLoc: loc,
                          returnLocationId: f.sameReturn
                            ? String(loc.locationId)
                            : f.returnLocationId,
                          returnLoc: f.sameReturn ? loc : f.returnLoc,
                        }))
                      }
                      placeholder={t('carRental.searchLocation', 'Havalimanı veya ofis ara...')}
                    />
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <Label>{t('carRental.returnLocation', 'İade')}</Label>
                        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                          <input
                            type="checkbox"
                            checked={form.sameReturn}
                            onChange={(e) =>
                              setForm((f) => ({
                                ...f,
                                sameReturn: e.target.checked,
                                returnLocationId: e.target.checked
                                  ? f.pickupLocationId
                                  : f.returnLocationId,
                                returnLoc: e.target.checked ? f.pickupLoc : f.returnLoc,
                              }))
                            }
                          />
                          {t('carRental.sameLocation', 'Aynı nokta')}
                        </label>
                      </div>
                      {form.sameReturn ? (
                        <Input
                          disabled
                          value={locLabel(pickupLoc) || '—'}
                          className="h-11"
                        />
                      ) : (
                        <LocationPicker
                          label=""
                          selectedLabel={locLabel(form.returnLoc)}
                          catalog={locationCatalog}
                          catalogLoading={catalogLoading}
                          ensureCatalog={ensureCatalog}
                          onSelect={(loc) =>
                            setForm((f) => ({
                              ...f,
                              returnLocationId: String(loc.locationId),
                              returnLoc: loc,
                            }))
                          }
                          placeholder={t('carRental.searchLocation', 'Havalimanı veya ofis ara...')}
                        />
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="space-y-1.5">
                      <Label>{t('carRental.startDate')}</Label>
                      <Input
                        type="date"
                        className="h-11"
                        value={form.pickupDate}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, pickupDate: e.target.value }))
                        }
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>{t('carRental.pickupTime', 'Alış saati')}</Label>
                      <Input
                        type="time"
                        className="h-11"
                        value={form.pickupTime}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, pickupTime: e.target.value }))
                        }
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>{t('carRental.endDate')}</Label>
                      <Input
                        type="date"
                        className="h-11"
                        value={form.returnDate}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, returnDate: e.target.value }))
                        }
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>{t('carRental.returnTime', 'İade saati')}</Label>
                      <Input
                        type="time"
                        className="h-11"
                        value={form.returnTime}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, returnTime: e.target.value }))
                        }
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                    <div className="space-y-1.5">
                      <Label>{t('carRental.driverAge', 'Sürücü yaşı')}</Label>
                      <Input
                        type="number"
                        min={18}
                        max={99}
                        className="h-11"
                        value={form.driverAge}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, driverAge: e.target.value }))
                        }
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Button
                        className="w-full h-11 bg-sky-700 hover:bg-sky-800"
                        onClick={handleSearch}
                        disabled={searching}
                      >
                        {searching ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Search className="w-4 h-4 mr-2" />
                        )}
                        {t('carRental.searchCars', 'Araç Ara')}
                      </Button>
                    </div>
                  </div>
                </>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="container mx-auto px-4 pb-16">
        {searched && (
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-slate-900">
              {t('carRental.availableCars')} ({vehicles.length})
            </h2>
          </div>
        )}

        {searching ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="overflow-hidden">
                <Skeleton className="h-48 w-full" />
                <CardContent className="p-4 space-y-3">
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : searched && vehicles.length === 0 ? (
          <Card className="p-12 text-center">
            <Car className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">{t('carRental.noCarsFound')}</h3>
            <p className="text-muted-foreground">{t('carRental.tryDifferentSearch')}</p>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {vehicles.map((car, idx) => {
              const daily = car.dailyPrice ?? car.dayPrice ?? car.pricePerDay;
              const total = car.totalPrice ?? car.total ?? daily;
              const currency = car.currencyCode || form.currencyCode || 'EUR';
              const vendor = vehicleVendorName(car);
              const logo = vehicleVendorLogo(car);
              const seats = vehicleSeatsLabel(car);
              const transmission = vehicleTransmissionLabel(car);
              const fuel = vehicleFuelLabel(car);
              const category = vehicleCategoryLabel(car);
              const typeName = vehicleTypeLabel(car);
              const conditions = vehicleConditions(car).slice(0, 3);
              const img = vehicleImageUrl(car);
              return (
                <Card
                  key={vehicleKey(car, idx)}
                  className={cn(
                    'overflow-hidden group hover:shadow-xl transition-all duration-300 border-slate-200/80'
                  )}
                >
                  <div className="relative h-48 overflow-hidden bg-slate-100">
                    <img
                      src={img}
                      alt={car.vehicleName || 'car'}
                      className="w-full h-full object-contain bg-white p-3 group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        e.currentTarget.src =
                          'https://images.unsplash.com/photo-1485291571150-772bcfc10da5?w=600';
                        e.currentTarget.className =
                          'w-full h-full object-cover group-hover:scale-105 transition-transform duration-500';
                      }}
                    />
                    <div className="absolute top-3 left-3 flex flex-wrap gap-1.5 max-w-[70%]">
                      {category && (
                        <Badge className="bg-sky-700">{category}</Badge>
                      )}
                      {typeName && (
                        <Badge variant="secondary">{typeName}</Badge>
                      )}
                    </div>
                    {logo ? (
                      <img
                        src={logo}
                        alt=""
                        className="absolute top-3 right-3 h-8 w-auto max-w-[72px] object-contain bg-white/90 rounded px-1"
                      />
                    ) : vendor ? (
                      <Badge className="absolute top-3 right-3 bg-slate-800/80 text-[10px] max-w-[40%] truncate">
                        {vendor}
                      </Badge>
                    ) : null}
                    {car.isAvailable === false && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <Badge variant="destructive">{t('carRental.notAvailable')}</Badge>
                      </div>
                    )}
                  </div>
                  <CardContent className="p-4 space-y-3">
                    <div>
                      <h3 className="font-bold text-lg leading-tight">
                        {car.vehicleName || car.sippCode || '—'}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {[car.sippCode, vendor].filter(Boolean).join(' · ')}
                      </p>
                      {car.vehicleDescription && (
                        <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                          {car.vehicleDescription}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1.5 text-sm text-muted-foreground">
                      {transmission && (
                        <span className="inline-flex items-center gap-1">
                          <Settings2 className="w-3.5 h-3.5" />
                          {transmission}
                        </span>
                      )}
                      {fuel && (
                        <span className="inline-flex items-center gap-1">
                          <Fuel className="w-3.5 h-3.5" />
                          {fuel}
                        </span>
                      )}
                      {seats && (
                        <span className="inline-flex items-center gap-1">
                          <Users className="w-3.5 h-3.5" />
                          {seats}
                        </span>
                      )}
                      {car.rentalDuration != null && (
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {car.rentalDuration} {t('carRental.day')}
                        </span>
                      )}
                      {car.totalKMLimit != null && Number(car.totalKMLimit) > 0 && (
                        <span className="inline-flex items-center gap-1">
                          <Gauge className="w-3.5 h-3.5" />
                          {car.totalKMLimit} km
                        </span>
                      )}
                      {car.isThereAirCondition && (
                        <span className="inline-flex items-center gap-1">
                          <Shield className="w-3.5 h-3.5" />
                          {t('carRental.ac', 'Klima')}
                        </span>
                      )}
                    </div>
                    {conditions.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {conditions.map((c) => (
                          <Badge
                            key={`${c.conditionId}-${c.conditionCode}`}
                            variant="outline"
                            className="text-[10px] font-normal"
                          >
                            {c.conditionName}
                          </Badge>
                        ))}
                      </div>
                    )}
                    <div className="flex items-end justify-between pt-2 border-t gap-3">
                      <div className="min-w-0">
                        {daily != null && (
                          <p className="text-xs text-muted-foreground">
                            {formatPrice(Number(daily), currency)} / {t('carRental.day')}
                          </p>
                        )}
                        <p className="text-xl font-bold text-sky-800">
                          {formatPrice(Number(total || 0), currency)}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          {t('carRental.totalPrice', 'Toplam')}
                          {car.depositPrice != null && Number(car.depositPrice) > 0 && (
                            <>
                              {' · '}
                              {t('carRental.deposit', 'Depozito')}:{' '}
                              {formatPrice(Number(car.depositPrice), currency)}
                            </>
                          )}
                        </p>
                        {car.vendorMinimumDriverAge != null && (
                          <p className="text-[11px] text-muted-foreground">
                            {t('carRental.minDriverAge', 'Min. yaş')}:{' '}
                            {car.vendorMinimumDriverAge}
                          </p>
                        )}
                      </div>
                      <Button
                        className="bg-sky-700 hover:bg-sky-800 shrink-0"
                        onClick={() => selectVehicle(car)}
                        disabled={car.isAvailable === false}
                      >
                        {t('carRental.bookNow')}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default CarRentalsPage;
